import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { AxiosError } from "axios";
import classnames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";
import {
  EventApiFactory,
  EventViewDto,
  NextDutyStatusEventDto,
  ResizeEventInputDto,
} from "../../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { CompactFormToolbar } from "../../../../common/form/CompactFormToolbar";
import { CompactSaveButton } from "../../../../common/form/CompactSaveButton";
import { HookFormTextInput } from "../../../../common/form/hook-form/inputs/HookFormTextInput";
import {
  addSeconds,
  differenceInSeconds,
  format,
  parse,
  parseISO,
} from "date-fns";

interface Props {
  record: EventViewDto;
  onStart: () => void;
  onSuccess: () => void;
  onFailed?: () => void;
}

const isDrivingEvent = (event: EventViewDto) => {
  return event.dutyStatus === "ChangeToDrivingStatus";
};

const getAverageSpeed = (
  event: EventViewDto,
  currentDuration?: string,
  nextMileage?: number
) => {
  if (!event.odometer || !nextMileage) {
    return undefined;
  }

  if (!currentDuration) {
    return undefined;
  }

  // The first event in the day will have been started in the previous day.
  // In that case, we always need to add this fixed duration in seconds to the calculation (it will be 0 in other cases),
  // So the average speed can be calculated correctly.
  const fixedDurationInSeconds =
    event.realDurationInSeconds! - event.durationInSeconds!;
  const durationInSeconds =
    differenceInSeconds(
      parse(currentDuration, "HH:mm:ss", new Date("2023-01-01 00:00:00")),
      new Date("2023-01-01 00:00:00")
    ) + fixedDurationInSeconds;

  const startTime = parseISO(event.startTime!);
  const nextEventTime = addSeconds(startTime, durationInSeconds);
  const seconds = differenceInSeconds(nextEventTime, startTime);
  const hours = seconds / 60 / 60;
  const miles = nextMileage - event.odometer;
  const mph = miles / hours;
  if (isNaN(mph)) {
    return undefined;
  }

  return mph.toFixed(2);
};

const getEventDurationAsString = (event: EventViewDto) => {
  if (!event.durationInSeconds) {
    return undefined;
  }

  const date = addSeconds(
    new Date("2023-01-01 00:00:00"),
    event.durationInSeconds
  );
  return format(date, "HH:mm:ss");
};

const DefaultCompactFormToolbar = () => {
  const {
    formState: { isSubmitting, isValid },
  } = useFormContext();
  return (
    <CompactFormToolbar>
      <CompactSaveButton
        saving={isSubmitting}
        disabled={isSubmitting || !isValid}
      />
    </CompactFormToolbar>
  );
};

export const ResizeEventForm = ({
  record,
  onSuccess,
  onFailed,
  onStart,
  ...rest
}: Props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const axios = useAxiosInstance();
  const [nextEvent, setNextEvent] = useState<NextDutyStatusEventDto | null>();
  const [loading, setLoading] = useState<boolean>();

  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!record?.id) {
      return;
    }

    if (!isDrivingEvent(record)) {
      return;
    }

    setLoading(true);
    EventApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppEventNextdriverdutystatuseventEventIdGet(record.id)
      .then((x) => setNextEvent(x.data))
      .catch((e) => {
        console.error(handleAxiosError(e));
        setNextEvent(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [axios, record]);

  const onSubmit = useCallback(
    (data: ResizeEventInputDto) => {
      if (!axios) {
        return;
      }

      onStart();
      return EventApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppEventResizeeventsPost({ ...data, eventId: record.id! })
        .then(() => {
          onSuccess();
        })
        .catch((e: AxiosError) => {
          if (onFailed) {
            onFailed();
          }

          notify(handleAxiosError(e), { type: "error" });
        });
    },
    [axios, notify, onSuccess, onStart, onFailed, record.id]
  );

  const formContext = useForm<ResizeEventInputDto>({
    mode: "onChange",
  });

  const { handleSubmit, control, watch } = formContext;
  const duration = watch("duration");

  const handleSubmitInternal: SubmitHandler<ResizeEventInputDto> = (data) => {
    return Promise.resolve(onSubmit(data)).catch((e: Error) => {
      notify(e.message ?? "An unexpected error has occurred", "error");
    });
  };

  return (
    <CardContent>
      <FormProvider {...formContext}>
        <form
          className={classnames("simple-form", "form-create-edit-drawer")}
          {...sanitizeRestProps(rest)}
          onSubmit={handleSubmit(handleSubmitInternal, (err) =>
            console.error(err)
          )}>
          <Card>
            <CardContent>
              <div className="ra-input">
                <HookFormTextInput<ResizeEventInputDto>
                  control={control}
                  name="duration"
                  label="Duty status duration (hh:mm:ss)"
                  rules={{
                    required: translate("ra.validation.required"),
                    pattern: {
                      value: /^\d+:[0-5]\d:[0-5]\d$/,
                      message: "Invalid time",
                    },
                  }}
                  variant="outlined"
                  defaultValue={getEventDurationAsString(record) ?? ""}
                  mask="99:99:99"
                />
              </div>
              {isDrivingEvent(record) ? (
                <div className="ra-input">
                  <Box display="flex" alignItems="center" gridGap={5}>
                    <Typography variant="body2">
                      Average speed (mph):
                    </Typography>
                    <Typography variant="body2">
                      {loading && !nextEvent ? (
                        <CircularProgress
                          size="0.875rem"
                          style={{ marginLeft: "5px" }}
                        />
                      ) : (
                        getAverageSpeed(
                          record,
                          duration,
                          nextEvent?.odometer ?? undefined
                        ) ?? "-"
                      )}
                    </Typography>
                  </Box>
                </div>
              ) : null}
            </CardContent>
          </Card>
          <DefaultCompactFormToolbar />
        </form>
      </FormProvider>
    </CardContent>
  );
};

const sanitizeRestProps = ({
  active,
  dirty,
  dirtyFields,
  dirtyFieldsSinceLastSubmit,
  dirtySinceLastSubmit,
  error,
  errors,
  form,
  hasSubmitErrors,
  hasValidationErrors,
  initialValues,
  modified = null,
  modifiedSinceLastSubmit,
  save = null,
  submitError,
  submitErrors,
  submitFailed,
  submitSucceeded,
  submitting,
  touched = null,
  valid,
  values,
  visited = null,
  __versions = null,
  ...props
}: any) => props;
